import React, { FunctionComponent, memo } from "react";
import { Grid } from "@mui/material";
import { MultiselectOption } from "@shared/components";
import useMultiselectHandlers from "@shared/hooks/useMultiselectHandlers";
import { useAppSelector, useMobileLayout } from "@app/hooks";
import SelfServiceMultiselect from "@myProfile/components/SelfServiceMultiselect";
import { updateCommonRequestData } from "../store/ducks";
import { requestContactUpdateSelector } from "../store/selectors";

interface Props {
  required?: boolean;
  funds: MultiselectOption[];
  accounts: MultiselectOption[];
  accountsLoading?: boolean;
  fundsLoading?: boolean;
}

const RequestContactUpdateEntitiesDropdowns: FunctionComponent<Props> = ({
  funds,
  accounts,
  required,
  accountsLoading = false,
  fundsLoading = false,
}) => {
  const { commonFields, validationWarningShown } = useAppSelector(requestContactUpdateSelector);
  const selectedAccounts = commonFields.selectedAccounts;
  const selectedFunds = commonFields.selectedFunds;

  const [handleSelectFund, handleClearAllFunds] = useMultiselectHandlers({
    entityName: "selectedFunds",
    nullIfEmpty: false,
    selectedValues: selectedFunds,
    action: updateCommonRequestData,
  });

  const [handleSelectAccount, handleClearAllAccounts] = useMultiselectHandlers({
    entityName: "selectedAccounts",
    nullIfEmpty: false,
    selectedValues: selectedAccounts,
    action: updateCommonRequestData,
  });
  const isMobile = useMobileLayout();

  return (
    <Grid item xs={12}>
      <Grid
        item
        container
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 4 : 7}
        data-testid="request-contact-update-entities-dropdowns"
      >
        <Grid item xs={6}>
          <SelfServiceMultiselect
            entity="account"
            size="large"
            variant="contained"
            options={accounts}
            onSelect={handleSelectAccount}
            onClearAll={handleClearAllAccounts}
            loading={accountsLoading}
            withSearch
            constrainToDialog
          />
          {required && validationWarningShown && !commonFields.selectedAccounts.length ? (
            <div style={{ color: "#AB8433", fontSize: 12, marginTop: 4 }}>Please select at least one account</div>
          ) : null}
        </Grid>
        <Grid item xs={6}>
          <SelfServiceMultiselect
            entity="fund"
            size="large"
            variant="contained"
            options={funds}
            onSelect={handleSelectFund}
            onClearAll={handleClearAllFunds}
            loading={fundsLoading}
            withSearch
            constrainToDialog
          />
          {required && validationWarningShown && !commonFields.selectedFunds.length ? (
            <div style={{ color: "#AB8433", fontSize: 12, marginTop: 4 }}>Please select at least one fund</div>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo<Props>(RequestContactUpdateEntitiesDropdowns);
